import { AbstractConnector } from 'bzx-common/../web3-react-6/packages/abstract-connector/dist'
import { ConnectorEvent, ConnectorUpdate } from 'bzx-common/../web3-react-6/packages/types/dist'
import { ChainId, isChainSupported } from '../config/appConfig'
import ContractsSource from '../contracts/ContractsSource'
import { ProviderChangedEvent } from '../domain/events/providerEvents'
import { getProviderTypeByConnector, ProviderType } from '../domain/ProviderTypeDictionary'
import Web3ConnectionFactory from '../services/Web3ConnectionFactory'
import providerUtils, { AnyProvider } from './providerUtils'

async function setWeb3ProviderFinalize(provider: AnyProvider, providerType?: ProviderType) {
  provider.web3Wrapper = Web3ConnectionFactory.web3Wrapper
  provider.providerEngine = Web3ConnectionFactory.providerEngine
  let canWrite = Web3ConnectionFactory.canWrite
  const networkId = Web3ConnectionFactory.networkId
  provider.accounts = Web3ConnectionFactory.userAccount ? [Web3ConnectionFactory.userAccount] : []
  const chainSupported = isChainSupported(networkId)

  if (provider.web3Wrapper && !isChainSupported(networkId)) {
    canWrite = false // revert back to read-only
  }

  if (provider.web3Wrapper && canWrite) {
    const web3EngineAccounts = await provider.web3Wrapper.getAvailableAddressesAsync()
    if (web3EngineAccounts.length > 0 && provider.accounts.length === 0) {
      provider.accounts = web3EngineAccounts
    }
    if (provider.accounts.length === 0) {
      canWrite = false // revert back to read-only
    }
  }

  if (provider.web3Wrapper && isChainSupported(networkId)) {
    const chainId = networkId as ChainId
    provider.contractsSource = new ContractsSource(provider.providerEngine, chainId, canWrite)
  } else {
    provider.contractsSource = null
  }

  if (providerType) {
    provider.providerType =
      canWrite || (!canWrite && !chainSupported) ? providerType : ProviderType.None
  }

  providerUtils.setLocalstorageItem('providerType', provider.providerType)
  providerUtils.setLocalstorageItem('userChainId', String(networkId))
  provider.emit(
    'ProviderChanged',
    new ProviderChangedEvent(provider.providerType, provider.web3Wrapper)
  )
}

async function setWeb3Provider(
  provider: AnyProvider,
  connector: AbstractConnector,
  account?: string
) {
  provider.emit('ProviderIsChanging')
  const providerType = await getProviderTypeByConnector(connector)
  await Web3ConnectionFactory.setWalletProvider(connector, providerType, account)
  await setWeb3ProviderFinalize(provider, providerType)
}

async function setReadonlyWeb3Provider(provider: AnyProvider, chainId: ChainId) {
  provider.emit('ProviderIsChanging')
  await Web3ConnectionFactory.setReadonlyProvider(chainId)
  await setWeb3ProviderFinalize(provider, ProviderType.None)
  // Walletconnect and walletlink keep info in localstorage that makes it reuse the same account
  // even after disconnecting.
  // So, remove the data on disconnect / readonly. It is a quick fix. Need further investigation.
  localStorage.removeItem('walletconnect')
  // walletlink
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.includes('walletlink')) {
      localStorage.removeItem(key)
    }
  }
}

async function onConnectorUpdated(provider: AnyProvider, update: ConnectorUpdate) {
  provider.emit('ProviderIsChanging')
  Web3ConnectionFactory.updateConnector(update)
  await setWeb3ProviderFinalize(provider)
}

async function getLibrary(provider: AnyProvider, connector: AbstractConnector) {
  // handle connectors events (i.e. network changed)
  await setWeb3Provider(provider, connector)
  if (!connector.listeners(ConnectorEvent.Update).includes(onConnectorUpdated)) {
    connector.on(ConnectorEvent.Update, (update) => update && onConnectorUpdated(provider, update))
  }
  return Web3ConnectionFactory.providerEngine
}

export default {
  getLibrary,
  onConnectorUpdated,
  setReadonlyWeb3Provider,
  setWeb3Provider,
  setWeb3ProviderFinalize,
}
